import { EObservationTimelineChange } from '@/composables/observationTimeline/observationTimelineQueryInvalidation';
import { ObservationTimelineEntryType } from '@/api/generated/api.enum';
import type { Components } from '@/api/generated/api';
import {
  bulkUpdateCompetenceObservationLevel,
  updateCompetenceObservation,
} from '@/api/resources/competenceObservation.api';
import { invalidatePerformanceAssessmentEntryQueries } from '@/composables/performanceAssessmentEntry/performanceAssessmentEntryQueryInvalidation';
import { useToast } from '@/composables/useToast';
import { useMutation, useQueryClient } from '@tanstack/vue-query';

interface IObservationMutationContext {
  schoolClassId: number;
  performanceAssessmentEntryId?: number;
}

type TUpdateObservation = Parameters<typeof updateCompetenceObservation>[0] & IObservationMutationContext;
type TBulkUpdateObservationLevels = Components.Schemas.BulkUpdateCompetenceObservationLevelDto &
  IObservationMutationContext;

/** Rating actions do not subscribe to observation lists or routed class/student state. */
export function useCompetenceObservationMutations() {
  const queryClient = useQueryClient();
  const { editSuccessToast, showError } = useToast();
  const onSuccess = async (
    _data: void,
    { schoolClassId, performanceAssessmentEntryId }: IObservationMutationContext,
  ): Promise<void> => {
    await invalidatePerformanceAssessmentEntryQueries(
      queryClient,
      schoolClassId,
      performanceAssessmentEntryId != null
        ? {
            kind: EObservationTimelineChange.Update,
            entity: { type: ObservationTimelineEntryType.performanceAssessmentEntry, id: performanceAssessmentEntryId },
          }
        : undefined,
    );
    editSuccessToast();
  };

  const update = useMutation({
    mutationFn: ({ observationId, dto }: TUpdateObservation) => updateCompetenceObservation({ observationId, dto }),
    onSuccess,
    onError: (error) => showError(error),
  });
  const bulkUpdate = useMutation({
    mutationFn: ({ observationIds, performanceLevelV2Id }: TBulkUpdateObservationLevels) =>
      bulkUpdateCompetenceObservationLevel({ observationIds, performanceLevelV2Id }),
    onSuccess,
    onError: (error) => showError(error),
  });

  return {
    updateCompetenceObservation: update.mutateAsync,
    bulkUpdateCompetenceObservationLevels: bulkUpdate.mutateAsync,
  };
}
